import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, Mail, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TwoFactorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);

  const { email, tempToken } = location.state || {};

  useEffect(() => {
    if (!email || !tempToken) {
      navigate('/login');
    }
  }, [email, tempToken, navigate]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...code];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);

    const nextEmpty = newCode.findIndex((c) => !c);
    if (nextEmpty !== -1) {
      inputRefs.current[nextEmpty]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    
    if (fullCode.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        email,
        code: fullCode,
        temp_token: tempToken
      });

      if (response.data.access_token) {
        login(response.data.access_token, response.data.user);
        toast.success('Login successful!');
        
        if (!response.data.user.consent_accepted) {
          navigate('/consent');
        } else if (['active', 'trialing'].includes(response.data.user.subscription_status)) {
          navigate('/sanctuary');
        } else if (response.data.user.current_phase) {
          navigate('/checkout');
        } else {
          navigate('/assessment');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-2fa`, {
        email,
        temp_token: tempToken
      });
      toast.success('New verification code sent!');
    } catch (error) {
      toast.error('Failed to resend code. Please try logging in again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Button
          onClick={() => navigate('/login')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to login
        </Button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
          </div>
          <h1
            className="text-3xl font-medium text-stone-800 mb-2"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Verify Your Identity
          </h1>
          <p className="text-stone-600">
            We sent a 6-digit code to
          </p>
          <p className="text-stone-800 font-medium flex items-center justify-center gap-2 mt-1">
            <Mail className="w-4 h-4" />
            {email}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-center gap-2">
            {code.map((digit, index) => (
              <Input
                key={`otp-digit-${index}`}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-12 h-14 text-center text-2xl font-medium rounded-xl border-stone-300 focus:border-emerald-500 focus:ring-emerald-500"
                data-testid={`otp-input-${index}`}
              />
            ))}
          </div>

          <Button
            type="submit"
            disabled={loading || code.join('').length !== 6}
            className="w-full rounded-full py-6 text-lg bg-stone-800 text-stone-50 hover:bg-stone-700"
            data-testid="verify-2fa-button"
          >
            {loading ? 'Verifying...' : 'Verify & Login'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-stone-500 text-sm mb-2">Didn't receive the code?</p>
          <Button
            onClick={handleResend}
            variant="ghost"
            disabled={resending}
            className="text-emerald-600 hover:text-emerald-700"
            data-testid="resend-code-button"
          >
            {resending ? 'Sending...' : 'Resend Code'}
          </Button>
        </div>

        <p className="text-xs text-stone-400 text-center mt-8">
          Code expires in 10 minutes
        </p>
      </div>
    </div>
  );
};
